/**
 * Stores Store - Pinia store for marketplace stores/shops
 * Manages store search, details, follow/favorite with optimistic updates, and selected store persistence
 */

import { defineStore } from "pinia";
import api from "@/services/api";
import type {
  StoresState,
  Store,
  SearchStoresParams,
  GetStoreOptions,
  GetStoreTagsOptions,
} from "./types/store.types";
import type { Tag, EcommerceOnboardingSummary, StoreDetailDto } from "@/types";
import { useProductsStore } from "./useProductsStore";
import { normalizeStore } from "@/utils/storeNormalize";
import { getErrorMessage } from "@/utils/errorUtils";

function normalizeTag(t: any = {}): Tag {
  const name = t.name || t.slug || "";
  return {
    name,
    slug: t.slug || name,
    icon: t.icon || "mdi-tag-outline",
    color: t.color || "primary",
    imageUrl: t.imageUrl || t.bannerUrl || t.logoUrl || undefined,
  };
}

/**
 * Get selected store from localStorage
 */
function getSelectedStoreFromStorage(): Store | null {
  try {
    const stored = localStorage.getItem("lyt-store-selected-store");
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error("Failed to load selected store from localStorage:", error);
    return null;
  }
}

export const useStoresStore = defineStore("stores", {
  state: (): StoresState => ({
    stores: [],
    detail: {},
    categories: {},
    tags: [],
    searchCriteria: {
      searchText: "",
      location: "",
      deliveryEnabled: false,
      pickupEnabled: false,
      minRating: 0,
      maxDeliveryFee: null,
      sortBy: "orders",
      sortDirection: "DESC",
      page: 1,
      pageSize: 20,
      tag: "",
    },
    following: new Set(),
    favorites: new Set(),
    pagination: {
      currentPage: 1,
      totalPages: 0,
      totalItems: 0,
      pageSize: 20,
    },
    selectedStore: getSelectedStoreFromStorage(),
    loading: {
      search: false,
      detail: false,
      categories: false,
      tags: false,
      follow: false,
      favorite: false,
      onboarding: false,
      activate: false,
    },
    error: {
      search: "",
      detail: "",
      categories: "",
      tags: "",
      follow: "",
      favorite: "",
      onboarding: "",
      activate: "",
    },
  }),

  getters: {
    /**
     * Get all stores
     */
    allStores(state): Store[] {
      return state.stores;
    },

    /**
     * Get store by ID
     */
    getStoreById(state) {
      return (id: string): Store | null => {
        const matchByKey =
          state.detail[id] ||
          state.detail[id?.toString?.()] ||
          state.detail[
            id
              ? id.toLowerCase?.() || id
              : id
          ];
        if (matchByKey) return matchByKey;

        const bySlug = state.stores.find(
          (s) => `${(s as any).slug || (s as any).storeSlug}` === `${id}`
        );
        if (bySlug) return bySlug;

        const byId = state.stores.find((s) => `${s.id}` === `${id}`);
        return byId || null;
      };
    },

    /**
     * Get selected store
     */
    currentSelectedStore(state): Store | null {
      return state.selectedStore;
    },

    /**
     * Get categories for a store
     */
    getStoreCategories(state) {
      return (storeId: string): any[] => {
        return state.categories[storeId] || [];
      };
    },

    /**
     * Get all store tags
     */
    storeTags(state): string[] {
      return state.tags.map((t) => t.name || t.slug);
    },

    /**
     * Get full tag objects with icon/color metadata
     */
    storeTagObjects(state): Tag[] {
      return state.tags;
    },

    /**
     * Check if following a store
     */
    isFollowing(state) {
      return (id: string): boolean => {
        return state.following.has(id);
      };
    },

    /**
     * Check if store is favorited
     */
    isFavorite(state) {
      return (id: string): boolean => {
        return state.favorites.has(id);
      };
    },

    /**
     * Get store filters
     */
    storeFilters(state) {
      return state.searchCriteria;
    },

    /**
     * Get store pagination
     */
    storePagination(state) {
      return state.pagination;
    },

    /**
     * Get loading states
     */
    storesLoading(state) {
      return state.loading;
    },

    /**
     * Get error states
     */
    storesError(state) {
      return state.error;
    },
  },

  actions: {
    /**
     * Select a store and persist to localStorage
     */
    selectStore(store: Store): Store {
      this.selectedStore = store;
      try {
        localStorage.setItem("lyt-store-selected-store", JSON.stringify(store));
      } catch (error) {
        console.error("Failed to persist selected store:", error);
      }
      try {
        const productsStore = useProductsStore();
        const slug = store.slug || (store as any).storeSlug || store.id;
        if (slug) {
          productsStore.fetchStoreCurrency(slug);
        }
      } catch (error) {
        console.warn("Failed to preload store currency", error);
      }
      return store;
    },

    /**
     * Clear selected store
     */
    clearSelectedStore() {
      this.selectedStore = null;
      try {
        localStorage.removeItem("lyt-store-selected-store");
      } catch (error) {
        console.error("Failed to remove selected store:", error);
      }
    },

    /**
     * Search stores with filters and pagination
     */
    async searchStores(params: SearchStoresParams = {}): Promise<Store[]> {
      this.loading.search = true;
      this.error.search = "";

      try {
        const criteria = { ...this.searchCriteria, ...params };

        // Build API request with correct parameter names
        const apiParams: any = {
          searchText: criteria.searchText || undefined,
          location: criteria.location || undefined,
          deliveryEnabled: criteria.deliveryEnabled || undefined,
          pickupEnabled: criteria.pickupEnabled || undefined,
          maxDeliveryFee: criteria.maxDeliveryFee || undefined,
          minFreeDeliveryThreshold:
            criteria.minFreeDeliveryThreshold || undefined,
          maxMinOrderAmount: criteria.maxMinOrderAmount || undefined,
          maxDeliveryRadius: criteria.maxDeliveryRadius || undefined,
          maxPreparationTime: criteria.maxPreparationTime || undefined,
          acceptsOnlinePayment: criteria.acceptsOnlinePayment || undefined,
          acceptsCashOnDelivery: criteria.acceptsCashOnDelivery || undefined,
          acceptsCashOnPickup: criteria.acceptsCashOnPickup || undefined,
          minRating: criteria.minRating || undefined,
          minReviewCount: criteria.minReviewCount || undefined,
          minFollowers: criteria.minFollowers || undefined,
          minTotalOrders: criteria.minTotalOrders || undefined,
          minRecentOrders: criteria.minRecentOrders || undefined,
          hasRecentActivity: criteria.hasRecentActivity || undefined,
          tagNames: criteria.tagNames || criteria.tag || undefined,
          tagSlugs: criteria.tagSlugs || undefined,
          sortBy: criteria.sortBy || undefined,
          sortDirection: criteria.sortDirection || "DESC",
          page: criteria.page || 1,
          pageSize: criteria.pageSize || 20,
        };

        const response = await api.stores.search(apiParams);

        // API returns { data: [...], pagination: {...} }
        const rawItems = response.data || [];
        const items = rawItems.map(s => normalizeStore(s as unknown));
        this.stores = items;
        this.searchCriteria = criteria;

        const p = response.pagination || {};
        this.pagination = {
          currentPage: p.currentPage || criteria.page || 1,
          totalPages:
            p.totalPages ||
            Math.ceil(
              (p.totalItems || items.length || 0) /
                (p.pageSize || criteria.pageSize || 20)
            ) ||
            1,
          totalItems: p.totalItems ?? items.length ?? 0,
          pageSize: p.pageSize || criteria.pageSize || 20,
        };

        return items;
      } catch (error: any) {
        this.error.search = error?.message || "Failed to load stores";
        return [];
      } finally {
        this.loading.search = false;
      }
    },

    /**
     * Get store details with cache
     */
    async getStore(
      id: string,
      options: GetStoreOptions = {}
    ): Promise<Store | null> {
      const { force = false } = options;

      if (!force && this.detail[id]) {
        return this.detail[id];
      }

      this.loading.detail = true;
      this.error.detail = "";

      try {
        const data = await api.stores.get(id);
        const store = normalizeStore((data?.data || data) as unknown);
        // Ensure keying by normalized id (slug)
        const slugKey =
          (store as any).slug ||
          (store as any).storeSlug ||
          id;
        const idKey = store.id ? `${store.id}` : null;

        this.detail[slugKey] = store;
        if (idKey && idKey !== slugKey) {
          this.detail[idKey] = store;
        }
        return store;
      } catch (error: any) {
        this.error.detail = error?.message || "Failed to load store";
        return null;
      } finally {
        this.loading.detail = false;
      }
    },

    /**
     * Get store categories with cache
     */
    async fetchStoreCategories(
      storeId: string,
      options: GetStoreOptions = {}
    ): Promise<any[]> {
      const { force = false } = options;

      if (!force && this.categories[storeId]) {
        return this.categories[storeId];
      }

      this.loading.categories = true;
      this.error.categories = "";

      try {
        const cats = await api.stores.categories(storeId);
        const items = cats.data || [];
        this.categories[storeId] = items;
        return items;
      } catch (error: any) {
        this.error.categories = error?.message || "Failed to load categories";
        return [];
      } finally {
        this.loading.categories = false;
      }
    },

    /**
     * Get global store tags with cache
     */
    async getStoreTags(options: GetStoreTagsOptions = {}): Promise<Tag[]> {
      const { force = false } = options;

      if (!force && this.tags && this.tags.length) {
        return this.tags;
      }

      this.loading.tags = true;
      this.error.tags = "";

      try {
        const tagsResponse = await api.stores.activeTags();
        const list = (tagsResponse as any)?.data || tagsResponse || [];
        this.tags = list
          .map((t: Tag) => normalizeTag(t))
          .filter((t: Tag) => t?.name);
        return this.tags;
      } catch (error: any) {
        this.error.tags = error?.message || "Failed to load tags";
        return [];
      } finally {
        this.loading.tags = false;
      }
    },

    /**
     * Follow a store (optimistic update with rollback on error)
     */
    async followStore(id: string): Promise<void> {
      this.loading.follow = true;
      const wasFollowing = this.following.has(id);

      try {
        // Optimistic update
        this.following.add(id);
        await api.stores.follow(id);
      } catch (error: any) {
        // Rollback on failure
        if (!wasFollowing) {
          this.following.delete(id);
        }

        // Handle authentication error with lazy import to avoid circular dependency
        if (error?.code === "AUTH_REQUIRED" || error?.status === 401) {
          const { useUserStore } = await import("./useUserStore");
          const userStore = useUserStore();

          userStore.queueAuthAction({
            type: "custom",
            handler: async () => await this.followStore(id),
          });

          userStore.showQuickLoginDialog({
            message:
              "Sign in to follow this store and receive notifications about new products",
            action: "follow",
            url: `/v1/followers/stores/${id}`,
          });
        } else {
          this.error.follow = error?.message || "Failed to follow store";
        }
      } finally {
        this.loading.follow = false;
      }
    },

    /**
     * Unfollow a store (optimistic update with rollback on error)
     */
    async unfollowStore(id: string): Promise<void> {
      this.loading.follow = true;
      const wasFollowing = this.following.has(id);

      try {
        // Optimistic update
        this.following.delete(id);
        await api.stores.unfollow(id);
      } catch (error: any) {
        // Rollback on failure
        if (wasFollowing) {
          this.following.add(id);
        }

        // Handle authentication error
        if (error?.code === "AUTH_REQUIRED" || error?.status === 401) {
          const { useUserStore } = await import("./useUserStore");
          const userStore = useUserStore();

          userStore.queueAuthAction({
            type: "custom",
            handler: async () => await this.unfollowStore(id),
          });

          userStore.showQuickLoginDialog({
            message: "Sign in to manage your followed stores and notifications",
            action: "unfollow",
            url: `/v1/followers/stores/${id}`,
          });
        } else {
          this.error.follow = error?.message || "Failed to unfollow store";
        }
      } finally {
        this.loading.follow = false;
      }
    },

    /**
     * Favorite a store (optimistic update with rollback on error)
     */
    async favoriteStore(id: string): Promise<void> {
      this.loading.favorite = true;
      const wasFavorite = this.favorites.has(id);

      try {
        // Optimistic update
        this.favorites.add(id);
        await api.stores.favorite(id);
      } catch (error: any) {
        // Rollback on failure
        if (!wasFavorite) {
          this.favorites.delete(id);
        }

        // Handle authentication error
        if (error?.code === "AUTH_REQUIRED" || error?.status === 401) {
          const { useUserStore } = await import("./useUserStore");
          const userStore = useUserStore();

          userStore.queueAuthAction({
            type: "custom",
            handler: async () => await this.favoriteStore(id),
          });

          userStore.showQuickLoginDialog({
            message:
              "Sign in to save this store to your favorites and access it quickly",
            action: "favorite",
            url: `/v1/favorites/stores`,
          });
        } else {
          this.error.favorite = error?.message || "Failed to favorite store";
        }
      } finally {
        this.loading.favorite = false;
      }
    },

    /**
     * Unfavorite a store (optimistic update with rollback on error)
     */
    async unfavoriteStore(id: string): Promise<void> {
      this.loading.favorite = true;
      const wasFavorite = this.favorites.has(id);

      try {
        // Optimistic update
        this.favorites.delete(id);
        await api.stores.unfavorite(id);
      } catch (error: any) {
        // Rollback on failure
        if (wasFavorite) {
          this.favorites.add(id);
        }

        // Handle authentication error
        if (error?.code === "AUTH_REQUIRED" || error?.status === 401) {
          const { useUserStore } = await import("./useUserStore");
          const userStore = useUserStore();

          userStore.queueAuthAction({
            type: "custom",
            handler: async () => await this.unfavoriteStore(id),
          });

          userStore.showQuickLoginDialog({
            message:
              "Sign in to manage your favorite stores and quick access list",
            action: "unfavorite",
            url: `/v1/favorites/stores`,
          });
        } else {
          this.error.favorite = error?.message || "Failed to unfavorite store";
        }
      } finally {
        this.loading.favorite = false;
      }
    },

    /**
     * Update store filters
     */
    setStoreFilters(partial: Partial<SearchStoresParams>) {
      this.searchCriteria = { ...this.searchCriteria, ...partial };
    },

    /**
     * Check onboarding status for a store
     * GET /v1/onboarding/{storeId}/status?invoice={invoiceNumber}
     */
    async checkOnboardingStatus(
      storeId: string,
      invoiceNumber: string
    ): Promise<EcommerceOnboardingSummary | null> {
      this.loading.onboarding = true;
      this.error.onboarding = "";
      try {
        const response = await api.onboarding.checkStatus(storeId, invoiceNumber);
        if (response.status === "SUCCESS") return response.data;
        this.error.onboarding =
          response.error?.message || "Failed to check onboarding status";
        return null;
      } catch (error: unknown) {
        this.error.onboarding = getErrorMessage(error);
        return null;
      } finally {
        this.loading.onboarding = false;
      }
    },

    /**
     * Activate / create a store via the onboarding endpoint.
     * When logoFile or bannerFile is provided, sends a multipart request;
     * otherwise sends a plain JSON request.
     * POST /v1/onboarding/{storeId}/{invoiceNumber}/activate
     */
    async activateStore(
      storeId: string,
      invoiceNumber: string,
      payload: StoreDetailDto,
      logoFile?: File | null,
      bannerFile?: File | null
    ): Promise<StoreDetailDto | null> {
      this.loading.activate = true;
      this.error.activate = "";
      try {
        const response =
          logoFile || bannerFile
            ? await api.onboarding.activateWithImages(
                storeId,
                invoiceNumber,
                payload,
                logoFile,
                bannerFile
              )
            : await api.onboarding.activate(storeId, invoiceNumber, payload);
        if (response.status === "SUCCESS") return response.data;
        this.error.activate =
          response.error?.message || "Failed to activate store";
        return null;
      } catch (error: unknown) {
        this.error.activate = getErrorMessage(error);
        return null;
      } finally {
        this.loading.activate = false;
      }
    },
  },
});
